import { Skeleton } from "@/components/shared/Skeleton";
import { useT } from "@/i18n";
import { Link } from "@tanstack/react-router";
import { HiChevronRight } from "react-icons/hi";
import { CLASSROOM_INDEX, type ClassroomScope } from "./classroomScope";

const LINK = "text-app-ink/60 hover:text-app-ink shrink-0 sm:shrink sm:truncate";
const TEXT = "font-semibold text-app-ink shrink-0 sm:shrink sm:truncate";

function Chevron() {
  return <HiChevronRight className="w-4 h-4 text-app-ink/40 shrink-0" />;
}

/**
 * Schools › school › classroom, for the pages above the student ones. A crumb is a skeleton until
 * its name arrives, and the deepest crumb is plain text because it is the page you are on.
 */
export function ClassroomBreadcrumb({
  scope,
  school,
  classroom,
  tail,
}: {
  scope: ClassroomScope;
  /** The school this page sits under, or null while its name loads. Omit when there is none. */
  school?: { id: string; name: string } | null | undefined;
  /** The classroom this page is about, or null while its name loads. Omit on a list page. */
  classroom?: string | null | undefined;
  /** A last crumb that is neither a school nor a classroom, such as the unscoped list. */
  tail?: string | undefined;
}) {
  const t = useT();
  const index = CLASSROOM_INDEX[scope];
  const deepest = tail ?? classroom;
  const hasDeepest = tail !== undefined || classroom !== undefined;

  return (
    <nav
      aria-label={t["nav.breadcrumb"]}
      className="flex items-center gap-1 body-sm min-w-0 overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      <Link to={index.to} className="text-app-ink/60 hover:text-app-ink shrink-0">
        {t[index.labelKey]}
      </Link>

      {school !== undefined && (
        <>
          <Chevron />
          {school === null ? (
            <Skeleton className="h-4 w-28 shrink-0" />
          ) : hasDeepest ? (
            <Link to="/admin/schools/$schoolId" params={{ schoolId: school.id }} className={LINK}>
              {school.name}
            </Link>
          ) : (
            <span className={TEXT}>{school.name}</span>
          )}
        </>
      )}

      {hasDeepest && (
        <>
          <Chevron />
          {deepest === null ? (
            <Skeleton className="h-4 w-24 shrink-0" />
          ) : (
            <span className={TEXT}>{deepest}</span>
          )}
        </>
      )}
    </nav>
  );
}
